import React, { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserId } from 'api/models';
import { PageLoading } from 'components/atoms/loading';
import { CurrentUserContext } from 'contexts/current-user';
import { useCurrentUserProfile } from 'data/current-user-profile/hooks';
import { useAccount } from 'hooks/use-account';
import { useCustomerPreview } from 'hooks/use-customer-preview';
import { useUpdateCurrentUserMutation } from 'queries';
import { fullName } from 'utils/full-name';
import { isHippaAccount } from 'utils/is-hippa-account';
import { getDevEnv } from 'utils/is-localhost';
import { isNitroEnvironment } from 'utils/is-nitro-env';
import { getCurrentUserTz, isValidTz } from 'utils/timezone';
import { setDataDogIdentity } from 'vendor/datadog-rum';
import { setupLogRocketIdentify } from 'vendor/log-rocket';

export const CurrentUserProvider: FC = (props) => {
  const { children } = props;
  const { data, loading } = useCurrentUserProfile();
  const account = useAccount();
  const { mutate } = useUpdateCurrentUserMutation();
  const currentUserTZ = getCurrentUserTz();
  const { isPreview, isPending } = useCustomerPreview();
  const { isLocal } = getDevEnv();

  useEffect(() => {
    if (isPending || isPreview) {
      return;
    }

    if (loading || data?.user == null) {
      return;
    }

    const {
      user: { timeZone },
    } = data;

    if (isValidTz(timeZone) && !isLocal) {
      mutate({ timeZone: currentUserTZ });
    }
  }, [data, loading, mutate, currentUserTZ, isPending, isPreview, isLocal]);

  useEffect(() => {
    if (loading || data?.user == null) {
      return;
    }

    const {
      user: { userId, emailId, ...user },
    } = data;

    setupLogRocketIdentify(
      {
        name: fullName(user),
        email: emailId,
        accountId: account.accountId,
        userId,
        emailId,
        fullName: fullName(user),
      },
      UserId(userId),
    );
  }, [data, loading, account]);

  return loading ? (
    <PageLoading />
  ) : data ? (
    <CurrentUserContext.Provider value={data}>
      {children}
    </CurrentUserContext.Provider>
  ) : (
    <FormattedMessage id="current_user.error" />
  );
};

export const useCurrentUserSideEffects = () => {
  const { data, loading } = useCurrentUserProfile();
  const account = useAccount();
  const { mutate } = useUpdateCurrentUserMutation();
  const currentUserTZ = getCurrentUserTz();
  const { isPreview, isPending } = useCustomerPreview();
  const { isLocal } = getDevEnv();

  useEffect(() => {
    if (isPending || isPreview) {
      return;
    }

    if (loading || data?.user == null) {
      return;
    }

    const {
      user: { timeZone },
    } = data;

    if (isValidTz(timeZone) && !isLocal) {
      mutate({ timeZone: currentUserTZ });
    }
  }, [data, loading, mutate, currentUserTZ, isPending, isPreview, isLocal]);

  useEffect(() => {
    if (loading || data?.user == null) {
      return;
    }

    const {
      user: { userId, emailId, ...user },
    } = data;

    setupLogRocketIdentify(
      {
        name: isHippaAccount() ? '' : fullName(user),
        email: isHippaAccount() ? '' : emailId,
        accountId: account.accountId,
        userId,
        emailId: isHippaAccount() ? '' : emailId,
        fullName: isHippaAccount() ? '' : fullName(user),
      },
      UserId(userId),
    );

    if (
      isNitroEnvironment() ||
      window.location.hostname.includes('leadsquared')
    ) {
      setDataDogIdentity({
        name: isHippaAccount() ? '' : fullName(user),
        userId,
        emailId: isHippaAccount() ? '' : emailId,
        accountId: account.accountId,
        id: account.defaultDomainName,
        domain: account.defaultDomainName,
        type: user.userType,
        build: isNitroEnvironment() ? 'staging' : 'production',
      });
    }
  }, [account.accountId, account.defaultDomainName, data, loading]);
};
